import React, { useState } from 'react';
import { 
  Settings, 
  Eye, 
  EyeOff, 
  Shield, 
  AlertTriangle,
  Pause,
  Play,
  Trash2,
  RefreshCw,
  QrCode,
  Link,
  Save,
  Loader2
} from 'lucide-react';

import { WebIDData, toggleWebIDStatus, reportWebIDLost } from '../../services/webids';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui';

/**
 * @component WebIDSettings
 * @description Configuración y ajustes de WebID
 */

interface WebIDSettingsProps {
  webId: WebIDData;
  onSave: (privacyData: { isPublic?: boolean }) => Promise<void>;
  onUpdate: () => void;
}

const WebIDSettings: React.FC<WebIDSettingsProps> = ({ webId, onSave, onUpdate }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Estados de configuración de privacidad
  const [privacySettings, setPrivacySettings] = useState({
    isPublic: webId.emergencyProfile.isPublic || false,
    showAllergies: false,
    showMedicalConditions: false,
    showMedications: false,
    showMedicalNotes: false,
    showEmergencyContacts: true,
    allowEmergencyAccess: true,
  });

  const handlePrivacyChange = (key: keyof typeof privacySettings, value: boolean) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSavePrivacy = async () => {
    try {
      setSaving(true);
      await onSave({ isPublic: privacySettings.isPublic });
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      alert('Error al guardar configuración de privacidad');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const newStatus = webId.status === 'active' ? 'paused' : 'active';
      await toggleWebIDStatus(webId.id, newStatus, user.uid);
      onUpdate();
    } catch (error) {
      console.error('Error toggling WebID status:', error);
      alert('Error al cambiar el estado de la pulsera');
    } finally {
      setLoading(false);
    }
  };

  const handleReportLost = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      '¿Estás seguro de que quieres reportar esta pulsera como perdida?\n\n' +
      'Esto desactivará el acceso público hasta que la recuperes. ' +
      'También se rotará la URL pública por seguridad.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await reportWebIDLost(webId.id, user.uid);
      onUpdate();
    } catch (error) {
      console.error('Error reporting WebID lost:', error);
      alert('Error al reportar la pulsera como perdida');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copiado al portapapeles');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Error al copiar al portapapeles');
    }
  };

  const generateQRCode = () => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(webId.publicUrl)}`;
    window.open(qrUrl, '_blank');
  };

  const getStatusColor = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'paused':
        return 'text-yellow-600';
      case 'lost':
        return 'text-red-600';
      case 'unclaimed':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'paused':
        return 'Pausada';
      case 'lost':
        return 'Perdida';
      case 'unclaimed':
        return 'Sin activar';
      default:
        return 'Desconocido';
    }
  };

  return (
    <div className="space-y-8">
      {/* Estado de la Pulsera */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Estado de la Pulsera
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Estado Actual</h4>
              <p className={`text-sm font-medium ${getStatusColor(webId.status)}`}>
                {getStatusText(webId.status)}
              </p>
            </div>
            
            <div className="flex gap-2">
              {webId.status === 'active' || webId.status === 'paused' ? (
                <Button
                  variant="outline"
                  onClick={handleToggleStatus}
                  disabled={loading}
                >
                  {webId.status === 'active' ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pausar
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Activar
                    </>
                  )}
                </Button>
              ) : null}

              {webId.status !== 'lost' && (
                <Button
                  variant="outline"
                  onClick={handleReportLost}
                  disabled={loading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Reportar Perdida
                </Button>
              )}
            </div>
          </div>

          {webId.status === 'paused' && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Pulsera pausada:</strong> El perfil público no está accesible. 
                Los escaneos NFC mostrarán un mensaje de pulsera temporalmente desactivada.
              </p>
            </div>
          )}

          {webId.status === 'lost' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Pulsera reportada como perdida:</strong> El acceso público ha sido 
                desactivado por seguridad. Contacta soporte si has recuperado tu pulsera.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Configuración de Privacidad */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Configuración de Privacidad
        </h3>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Perfil Público</h4>
                <p className="text-sm text-gray-600">
                  Permite que cualquier persona vea tu perfil de emergencia
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacySettings.isPublic}
                  onChange={(e) => handlePrivacyChange('isPublic', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Mostrar Condiciones Médicas</h4>
                  <p className="text-sm text-gray-600">
                    Incluir condiciones médicas como diabetes, hipertensión, etc.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.showMedicalConditions}
                    onChange={(e) => handlePrivacyChange('showMedicalConditions', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Mostrar Medicamentos</h4>
                  <p className="text-sm text-gray-600">
                    Incluir medicamentos actuales y dosis
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.showMedications}
                    onChange={(e) => handlePrivacyChange('showMedications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Mostrar Notas Médicas</h4>
                  <p className="text-sm text-gray-600">
                    Incluir notas adicionales, doctor de cabecera, hospital preferido
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacySettings.showMedicalNotes}
                    onChange={(e) => handlePrivacyChange('showMedicalNotes', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Mostrar Alergias</h4>
                <p className="text-sm text-gray-600">
                  Mostrar alergias conocidas en el perfil público
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacySettings.showAllergies}
                  onChange={(e) => handlePrivacyChange('showAllergies', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Mostrar Contactos de Emergencia</h4>
                <p className="text-sm text-gray-600">
                  Permitir que los primeros auxilios vean tus contactos de emergencia
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacySettings.showEmergencyContacts}
                  onChange={(e) => handlePrivacyChange('showEmergencyContacts', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">Acceso de Emergencia</h4>
                <p className="text-sm text-gray-600">
                  Permitir acceso completo al perfil en situaciones de emergencia (recomendado)
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacySettings.allowEmergencyAccess}
                  onChange={(e) => handlePrivacyChange('allowEmergencyAccess', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              onClick={handleSavePrivacy}
              disabled={saving}
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Configuración
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* URLs y Códigos */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Link className="h-5 w-5" />
          URLs y Códigos
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL Pública del Perfil
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={webId.publicUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(webId.publicUrl)}
              >
                Copiar
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={generateQRCode}
              className="flex-1"
            >
              <QrCode className="h-4 w-4 mr-2" />
              Generar Código QR
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.open(webId.publicUrl, '_blank')}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-2" />
              Ver Perfil Público
            </Button>
          </div>
        </div>
      </div>

      {/* Información de Seguridad */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">
              Información de Seguridad
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Los cambios de privacidad se aplican inmediatamente</li>
              <li>• El acceso de emergencia permite a los primeros auxilios ver toda tu información médica</li>
              <li>• Pausar la pulsera mantiene tus datos seguros pero impide el acceso en emergencias</li>
              <li>• Reportar como perdida desactiva completamente el acceso público</li>
              <li>• Todos los accesos quedan registrados en el historial</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIDSettings;
